import { useEffect, useState } from "react"

import { useQuery } from "@apollo/client"

import { graphql } from "@/main/gql/__generated__"
import { CancelForListFragment } from "@/main/gql/__generated__/graphql"

const GetNotClaimedCancelsNotificationLogDisplayedCancelIds = graphql(
	/* GraphQL */ `
		query GetNotClaimedCancelsNotificationLogDisplayedCancelIds(
			$param: NotClaimedCancelsNotificationLogDisplayedCancelIdsParam!
		) {
			notClaimedCancelsNotificationLogDisplayedCancelIds(param: $param) {
				displayedCancelIds
			}
		}
	`
)

type UseNotificationBulkClaimProps = {
	invoiceNotSentCancels: readonly CancelForListFragment[]
	/** GetNotClaimedCancels の loading が完了したか（true = ロード完了） */
	isInvoiceNotSentCancelsLoaded: boolean
	setCheckedNotSentInvoiceCancelMap: (
		map: Record<number, CancelForListFragment>
	) => void
	onClickBundleClaimButton: () => void
}

export const useNotificationBulkClaim = ({
	invoiceNotSentCancels,
	isInvoiceNotSentCancelsLoaded,
	setCheckedNotSentInvoiceCancelMap,
	onClickBundleClaimButton,
}: UseNotificationBulkClaimProps): void => {
	const [hasTriggered, setHasTriggered] = useState(false)
	const [shouldAutoTrigger, setShouldAutoTrigger] = useState(false)

	// 1. URL から notification_log_id を読み取る（初回マウント時のみ）
	const [notificationLogId] = useState(() =>
		new URLSearchParams(window.location.search).get("notification_log_id")
	)

	// 2. notification_log_id がある場合に GraphQL Query を実行
	const { data, error } = useQuery(
		GetNotClaimedCancelsNotificationLogDisplayedCancelIds,
		{
			variables: { param: { id: notificationLogId ?? "" } },
			skip: !notificationLogId,
		}
	)

	// 3. Query 結果と invoiceNotSentCancels の交差フィルタ → checkedMap 構築 → 自動トリガー
	useEffect(() => {
		if (hasTriggered || !notificationLogId) return

		// GraphQL エラー時はサイレントフォールバック
		if (error) {
			setHasTriggered(true)
			return
		}

		if (!data) return

		const displayedCancelIds =
			data.notClaimedCancelsNotificationLogDisplayedCancelIds.displayedCancelIds

		// displayedCancelIds が空 → ログ不存在 or salon_id 不一致 → サイレントフォールバック
		if (displayedCancelIds.length === 0) {
			setHasTriggered(true)
			return
		}

		// invoiceNotSentCancels のロードが完了していない場合は待機
		if (!isInvoiceNotSentCancelsLoaded) return

		// displayedCancelIds (string[] = cancel.idv2 = UUID) と
		// invoiceNotSentCancels の idv2 で交差フィルタ
		const displayedCancelIdSet = new Set(displayedCancelIds)
		const targetCancels = invoiceNotSentCancels.filter((cancel) =>
			displayedCancelIdSet.has(cancel.idv2)
		)

		if (targetCancels.length === 0) {
			// 全件請求済み
			alert("通知メールに記載されたキャンセルは全て請求済みです。")
			setHasTriggered(true)
			return
		}

		// checkedNotSentInvoiceCancelMap を自動構築
		const cancelMap: Record<number, CancelForListFragment> = {}
		targetCancels.forEach((cancel) => {
			cancelMap[cancel.id] = cancel
		})
		setCheckedNotSentInvoiceCancelMap(cancelMap)

		// 次の render cycle で onClickBundleClaimButton を呼ぶ
		setShouldAutoTrigger(true)
		setHasTriggered(true)
	}, [
		data,
		error,
		invoiceNotSentCancels,
		isInvoiceNotSentCancelsLoaded,
		hasTriggered,
		notificationLogId,
		setCheckedNotSentInvoiceCancelMap,
	])

	// NOTE: onClickBundleClaimButton は
	// setCheckedNotSentInvoiceCancelMap(cancelMap)
	// が反映された後に再作成される必要があるので、
	// setShouldAutoTrigger の行で直接 onClickBundleClaimButton を読んではいけない
	useEffect(() => {
		if (shouldAutoTrigger) {
			onClickBundleClaimButton()
			setShouldAutoTrigger(false)
		}
	}, [shouldAutoTrigger])
}
